import { List } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import { findDOMNode } from "react-dom";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import EmptyState from "../../components/emptyState";
import { withScreenContext } from "../../components/screen/ScreenContext";
import TableView from "../../components/tableView";
import tableViewUtils from "../../components/tableView/utils";
import { GLOBAL_FILTER_ID } from "../../constants/identifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import outlineColumnIds, { outlineColumnLabels } from "../../constants/outlineColumnIds";
import PubSubTopics from "../../constants/pubSubTopics";
import tableDropAction from "../../constants/tableDropAction";
import TableRowTypes from "../../constants/tableRowTypes";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { stringFromDate } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import { colorSelectors } from "../../modules/definition/colors";
import { propertySelectors } from "../../modules/definition/properties";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
// import debugUtils from "../../utils/debugUtils";
import displayCacheSelectors from "../../modules/display/selectors";
import dropSelectors from "../../modules/dropSelectors";
import { filterSelectors } from "../../modules/filters";
import { itemOperations, itemSelectors } from "../../modules/items";
import { sequenceOperations, sequenceSelectors } from "../../modules/sequence";
import { timelineReducer } from "../../modules/timelineReducer";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { immutableRecords, PropTypeShapes } from "../../types";
import { selectByPubSubTopics } from "../../types/immutableRecords/model/appState/utils";
import { chooseValueForIos, mergeDispatchToProps } from "../../utils/codeSplit";
import { onPasteItems } from "../../utils/copyPaste";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import { getColumnLimitedWidth } from "../../utils/tableColumns";
import { trackEvent } from "../../utils/trackAnalytics";
import { appSelectors } from "../app";
import tableViewSelectors from "../sharedSelectors/tableViewSelectors";
import { withWorkspaceContext } from "../workspace/WorkspaceContext";
import selectors from "./selectors";

const tableDataWithoutRowIds = (data, rowIds) => {
  return rowIds && rowIds.length ? data.update("rows", rows => rows.filter(r => rowIds.indexOf(r.id) >= 0)) : data;
};

class SpreadsheetView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      data: immutableRecords.TableRecord({}),
      displayData: immutableRecords.TableRecord({}),
      draggedItemIds: List(),
      sortableColumnIds: List()
    };
    this._lastAction = null;
    this._tableView = null;
    this.dispatchLocal = this.dispatchLocal.bind(this);
    this.startRowDrag = this.startRowDrag.bind(this);
    this.onMoveRow = this.onMoveRow.bind(this);
    this.internalMoveColumn = this.internalMoveColumn.bind(this);
    this.internalDragLeave = this.internalDragLeave.bind(this);
    this.dispatchLastMoveAction = this.dispatchLastMoveAction.bind(this);
    this.requestCanDropItems = this.requestCanDropItems.bind(this);
    this.onCanItemDropOnRow = this.onCanItemDropOnRow.bind(this);
    this.onCreateItem = this.onCreateItem.bind(this);
    this.onTableValueChanged = this.onTableValueChanged.bind(this);
    this.onTableMultipleValueChanged = this.onTableMultipleValueChanged.bind(this);
    this.onTableBulkChange = this.onTableBulkChange.bind(this);
    this.onSelectItemByTopicsReceived = this.onSelectItemByTopicsReceived.bind(this);
    this.onScrollToItemReceived = this.onScrollToItemReceived.bind(this);
    this.onScrollToDateReceived = this.onScrollToDateReceived.bind(this);
    this.dragPreviewDataForItemId = this.dragPreviewDataForItemId.bind(this);
    this.onToggleChildren = this.onToggleChildren.bind(this);
    this.onToggleColumn = this.onToggleColumn.bind(this);
    this.onResizeColumn = this.onResizeColumn.bind(this);
    this.onSortByColumn = this.onSortByColumn.bind(this);
    this.getCellOptions = this.getCellOptions.bind(this);
    this.onReceivedFocus = this.onReceivedFocus.bind(this);
    this.onViewClick = this.onViewClick.bind(this);
    this.canTypeHaveSuperType = this.canTypeHaveSuperType.bind(this);

    // paste
    this.onItemPaste = this.onItemPaste.bind(this);
    this.processPastingItems = this.processPastingItems.bind(this);

    this.getItemIdsToExpand = this.getItemIdsToExpand.bind(this);
  }

  componentWillMount() {
    this.copyLocalStore(this.props);
  }

  componentDidMount() {
    this._node = findDOMNode(this);
    this._pubSubScrollToItemToken = PubSub.subscribe(PubSubTopics.scrollToItem, this.onScrollToItemReceived);
    this._pubSubScrollToDateToken = PubSub.subscribe(PubSubTopics.scrollToDate, this.onScrollToDateReceived);
    this._pubSubSelectItemByTopicsToken = PubSub.subscribe(
      PubSubTopics.selectItemByTopics,
      this.onSelectItemByTopicsReceived
    );
    this.props.workViewRegistration.registerWorkViewCreateItemCallback(this.props.viewId, this.onCreateItem);
  }

  componentWillReceiveProps(nextProps) {
    // debugUtils.logPropChanges("SpreadsheetView", this.props, nextProps);
    this.copyLocalStore(nextProps);
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubScrollToItemToken);
    PubSub.unsubscribe(this._pubSubScrollToDateToken);
    this.props.workViewRegistration.unregisterWorkViewCreateItemCallback(this.props.viewId);
    PubSub.unsubscribe(this._pubSubSelectItemByTopicsToken);
  }

  onSelectItemByTopicsReceived(msg, selectTopic) {
    const newSelectedIds = selectByPubSubTopics(
      this.props.isActiveView,
      this.state.data.rows.filter(row => row.id !== TableRowTypes.EMPTY),
      selectTopic,
      this.props.selectedItemIds
    );
    if (newSelectedIds.length > 0) {
      this.props.onSelectItems(newSelectedIds);
      this.onScrollToItemReceived("", { id: newSelectedIds[0] });
    }
  }

  onScrollToItemReceived(msg, data) {
    this.scrollToRow(data.id);
  }

  scrollToRow(rowId) {
    if (this._tableView) {
      this._tableView.scrollToRow(rowId);
    }
  }

  onScrollToDateReceived(_msg, date) {
    // Scroll to the first node on or after the specified date
    if (date) {
      let lastDatedItem;
      const row = this.state.data.rows
        .filter(row => row.id != TableRowTypes.EMPTY)
        .find(row => {
          const item = this.props.displayItems.get(row.id);
          if (isDateValid(item.startDate)) {
            lastDatedItem = item;
            return item.startDate.timestamp >= date.timestamp;
          }
          return false;
        });
      if (row) {
        this.scrollToRow(row.id);
        return;
      }
      // If all items are before the specified date, scroll to the last row with a date
      if (lastDatedItem) {
        this.scrollToRow(lastDatedItem.id);
      }
    }
  }

  canCreateItemOfType(typeId) {
    return typeSelectors.getTypeIsAllowedInSequence(this.props.localStore, typeId);
  }

  onCreateItem(typeId) {
    const lastSelectedRow = this.state.data.rows.findLast(r => this.props.selectedItemIds.contains(r.id));

    const parentRowId = lastSelectedRow ? lastSelectedRow.parentRowId : null;
    // If this type is not allowed in sequence, then don't add it here.
    if (!this.canCreateItemOfType(typeId)) {
      return false;
    }
    // Otherwise, we can add it.
    // Do we want to add it after current selection?
    if (lastSelectedRow) {
      const sequenceIndex = sequenceSelectors.getItemSequenceIndex(this.props.localStore, lastSelectedRow.id);
      this.props.onCreateItemInSequence(typeId, parentRowId, sequenceIndex + 1, null, this.props.viewId);
    } else {
      this.props.onCreateItemInSequence(typeId, null, null, null, this.props.viewId);
    }
    return true;
  }

  onTableValueChanged(itemId, columnId, value) {
    if (itemId !== TableRowTypes.EMPTY) {
      trackEvent("setItemValueFromSpreadsheetView", "setItemValue", columnId);
      this.props.onValueChanged(itemId, columnId, value, this.props.localStore);
      return;
    }
    const typeId = this.props.defaultType;
    // Otherwise, we can add it.
    // Do we want to add it after current selection?
    this.props.onCreateItemInSequence(typeId, null, null, value, this.props.viewId);
  }

  onTableMultipleValueChanged(itemId, columnId, value, newComponentState) {
    this.props.onMultiValueChanged(itemId, columnId, value, this.props.localStore, newComponentState);
  }

  onTableBulkChange(newRowIds, changes) {
    const defaultTypeId = this.props.defaultType;
    this.props.onBulkChange(newRowIds, changes, defaultTypeId, this.props.localStore);
  }

  copyLocalStore(props) {
    const sortableColumnIds = selectors.getSortableColumnIds(props.localStore);
    const data = selectors.getSpreadsheetViewData(props.localStore, props.viewId);
    const displayData = data;

    this.setState({
      data,
      displayData,
      draggedItemIds: List(),
      sortableColumnIds
    });
  }

  dispatchLocal(action, draggedIds) {
    // Dispatch local needs to apply to the original state each time, so we always apply it to props.localStore instead
    const newStore = timelineReducer(this.props.localStore, action, this.props.localStore);
    const data = selectors.getSpreadsheetViewData(newStore, this.props.viewId);
    const displayData = tableDataWithoutRowIds(data, draggedIds);

    this.setState({
      data,
      displayData,
      draggedItemIds: List(draggedIds)
    });
  }

  internalDragLeave() {
    this.copyLocalStore(this.props);
  }

  startRowDrag(itemIds) {
    // const displayData = tableDataWithoutRowIds(this.state.data, itemIds);
    this.setState({
      // displayData,
      draggedItemIds: List(itemIds)
    });
  }

  internalMoveColumn(column, index) {
    const action = appStateOperations.moveSpreadsheetColumn(this.props.viewId, column, index);
    if (_.isEqual(action, this._lastAction)) {
      return;
    }
    this.dispatchLocal(action, []);
    this._lastAction = action;
  }

  requestCanDropItems(
    itemIds,
    dropRows,
    dropDate,
    dropAction,
    dropRow,
    newDropRowIndex,
    adjustItemEndWhenChangingStart
  ) {
    if (!itemSelectors.getItemsAreAllowedInSequence(this.props.localStore, itemIds)) {
      return {
        canDrop: false,
        message: interfaceMessages.DRAG_DROP_DATE_ORDER_NOT_ALLOWED
      };
    }

    // If not sorted by sequence, only allow dropping on a parent or top level
    if (this.props.sortBy !== outlineColumnIds.sequence) {
      if (dropAction !== tableDropAction.DropOnRow && dropAction !== tableDropAction.DropTopLevel) {
        return {
          canDrop: false,
          message: `${interfaceMessages.DRAG_DROP_CANNOT_REORDER_DUE_TO_SORT}${outlineColumnLabels[this.props.sortBy]}`
        };
      } else if (dropAction === tableDropAction.DropTopLevel) {
        return {
          canDrop: true,
          message: null
        };
      }
    }

    let newDropDateString;
    const newSequenceIndex = sequenceSelectors.getItemSequenceIndex(this.props.localStore, dropRow.id);
    const dropSequenceIndex = dropAction === tableDropAction.DropBelowRow ? newSequenceIndex + 1 : newSequenceIndex;
    for (let i = 0; i < itemIds.length; i++) {
      const itemId = itemIds[i];
      const item = this.props.displayItems.get(itemId);

      // If the drag will change the item's date, show it in the drag message
      if (isDateValid(dropDate)) {
        if (isDateValid(item.startDate) && item.startDate.timestamp !== dropDate.timestamp) {
          const sequenceIndex = sequenceSelectors.getItemSequenceIndex(this.props.localStore, item.id);
          if (sequenceIndex !== dropSequenceIndex) {
            const targetObj = this.props.itemsById.get(dropRow.id);
            const superId = targetObj ? targetObj.super : null;

            const newDropDate = dropSelectors.getDropDateChange(
              this.props.itemSequence,
              dropSequenceIndex,
              item.id,
              item,
              itemIds,
              this.props.itemsById,
              superId
            );

            if (newDropDate && newDropDate.timestamp != item.startDate.timestamp) {
              newDropDateString = `Move to: ${stringFromDate(this.props.calendar, newDropDate)}`;

              // Block the drop if any dates are locked
              if (item.isDateLocked) {
                return {
                  canDrop: false,
                  message: interfaceMessages.DRAG_DROP_LOCKED_DATES
                };
              }
            }
          }
        }
      }
    }

    // If !adjustItemEndWhenChangingStart, only allow the drop if it isn't later than dates other than start date
    if (!adjustItemEndWhenChangingStart && dropDate) {
      const rows = dropRows.filter(row => itemIds.includes(row.id));
      for (let i = 0; i < rows.size; i++) {
        const row = rows.get(i);
        const item = this.props.displayItems.find(item => item.id === row.id);
        if (item) {
          if (
            isDateValid(item.endDate) &&
            item.endDate.timestamp != item.startDate.timestamp &&
            dropDate.timestamp > item.endDate.timestamp
          ) {
            return {
              canDrop: false,
              message: interfaceMessages.DRAG_DROP_END_DATE_NOT_ALLOWED
            };
          }

          if (
            isDateValid(item.latestStartDate) &&
            item.latestStartDate.timestamp != item.startDate.timestamp &&
            dropDate.timestamp > item.latestStartDate.timestamp
          ) {
            return {
              canDrop: false,
              message: interfaceMessages.DRAG_DROP_LATEST_START_DATE_NOT_ALLOWED
            };
          }

          if (
            isDateValid(item.earliestEndDate) &&
            item.earliestEndDate.timestamp != item.startDate.timestamp &&
            dropDate.timestamp > item.earliestEndDate.timestamp
          ) {
            return {
              canDrop: false,
              message: interfaceMessages.DRAG_DROP_EARLIEST_END_DATE_NOT_ALLOWED
            };
          }
        }
      }
    }
    return { canDrop: true, message: newDropDateString ? newDropDateString : null };
  }

  onMoveRow(itemIds, dropRow, dropAction, fromExternalSource) {
    const { localStore, sortBy } = this.props;
    let action = dropSelectors.getActionForSpreadsheetDrop(localStore, itemIds, dropRow.id, dropAction, sortBy);
    if (action) {
      // If the drag source was outside this view, then we want to auto-apply filters
      if (fromExternalSource) {
        action = batchOperations.matchFiltersAfterDropAction(action, itemIds, this.props.viewId);
      }

      this.props.onDispatchAction(action);

      // TO DO: Differentiate between group and reorder
      trackEvent("setItemOrderOrGroupByDragFromSpreadsheetView", "setItemOrderOrGroupByDrag");
    }
  }

  onCanItemDropOnRow(dragItem, dropRow) {
    // Ensure that the two item types are allowed a super-child relationship
    // We do not check for a super loop here, as the table view takes care of it now itself.
    // TODO: Note that when you try to transfer this functionality across to Relationship View,
    // TODO: you will need to take care of it again :-)
    const childType = dragItem.itemType;
    const superType = dropRow.type;
    return typeSelectors.canTypeHaveSuperType(this.props.allTypesById, childType, superType);
  }

  onToggleChildren(rowIndex, isExpanded) {
    this.props.onToggleChildren(this.props.viewId, this.state.data.rows.get(rowIndex).id, isExpanded);
  }

  onToggleColumn(column, showColumn) {
    this.props.onToggleColumn(this.props.viewId, column, showColumn);
  }

  onResizeColumn(column, width) {
    const limitedWidth = getColumnLimitedWidth(column.id, width);
    this.props.onResizeColumn(this.props.viewId, column, limitedWidth);
  }

  onSortByColumn(columnId) {
    this.props.onSortByColumn(this.props.viewId, columnId);
  }

  dispatchLastMoveAction() {
    if (this._lastAction) {
      this.props.onDispatchAction(this._lastAction);
    }
    this.setState({
      displayData: this.state.data,
      draggedItemIds: List()
    });
    this._lastAction = null;
  }

  dragPreviewDataForItemId(itemId) {
    return selectors.getSpreadsheetViewDataForItem(
      this.props.localStore,
      this.props.viewId,
      this.state.data,
      itemId,
      this.props.itemsById,
      this.props.allTypesById
    );
  }

  getCellOptions(rowId, columnId) {
    return selectors.getSpreadsheetViewOptionsForColumns(this.props.localStore, rowId, columnId);
  }

  onReceivedFocus() {
    if (!this.props.isActiveView) {
      this.props.onBecameActiveView(this.props.viewId);
    }
  }

  onViewClick(e) {
    const clickWasItem = hasAncestorOfClass(e.target, "tableRow");
    if (!clickWasItem && this.props.selectedItemIds.size > 0) {
      this.props.onViewClick();
    }
  }

  canTypeHaveSuperType(childType, superType) {
    return typeSelectors.canTypeHaveSuperType(this.props.allTypesById, childType, superType);
  }

  processPastingItems(itemMapList) {
    const { rows } = this.state.data;

    const lastSelectedRow = rows.findLast(r => this.props.selectedItemIds.contains(r.id));
    const superId = lastSelectedRow && lastSelectedRow.parentRowId ? lastSelectedRow.parentRowId : null;

    const totalRowsCount = rows.size;
    const lastRow = totalRowsCount > 1 ? rows.get(totalRowsCount - 2) : null;

    let sequenceIndex = sequenceSelectors.getItemSequenceIndex(
      this.props.localStore,
      lastSelectedRow ? lastSelectedRow.id : lastRow ? lastRow.id : null
    );

    let extraActions = [];
    itemMapList.forEach(im => {
      if (superId) {
        extraActions.push(itemOperations.setSuper([im.get("id")], superId));
      }
      sequenceIndex += 1;
      extraActions.push(sequenceOperations.moveItemsInSequence([im.get("id")], sequenceIndex));
    });

    return extraActions;
  }

  onItemPaste(e) {
    const {
      dispatchPasteActions,
      projectId,
      isActiveView,
      timelineTypeIds,
      defaultType,
      allTypesById,
      propertiesById,
      colorsById,
      referencesById,
      itemsById
    } = this.props;
    dispatchPasteActions(
      onPasteItems(
        e,
        projectId,
        isActiveView,
        itemMap => this.canCreateItemOfType(itemMap.get("type")),
        this.processPastingItems,
        timelineTypeIds,
        defaultType,
        typeSelectors.isDatedType,
        allTypesById,
        propertiesById,
        colorsById,
        referencesById,
        itemsById
      )
    );
  }

  isSequenceType(typeId) {
    const type = this.props.allTypesById.get(typeId);
    return typeSelectors.isSequenceType(type);
  }

  getItemIdsToExpand(itemId) {
    const { itemsById, viewId, onToggleChildren } = this.props;
    const { rows } = this.state.data;
    let itemIdsToExpand = [];

    // Tries to scroll to sequence items only
    if (this.isSequenceType(itemsById.get(itemId).type)) {
      const findToExpandSuper = id => {
        const targetItem = itemsById.get(id);
        if (targetItem && targetItem.super) {
          const superId = targetItem.super;
          itemIdsToExpand.push(superId);
          if (!rows.find(r => r.id === superId)) {
            // The super is not in position (i.e not expanded)
            // find the super of super
            findToExpandSuper(superId);
          }
        }
      };

      findToExpandSuper(itemId);
      if (itemIdsToExpand.length > 0) {
        onToggleChildren(viewId, itemIdsToExpand, true);
      }
    }

    return itemIdsToExpand;
  }

  render() {
    const canRowDrop = true;
    let allowRowDrag = true;
    //#!if iOS === true
// allowRowDrag = this.props.screenContext.isEditMode;
    //#!endif

    const style = {
      display: "flex",
      width: `${this.props.containerRect.width}px`,
      height: `${this.props.containerRect.height}px`,
      minHeight: "500px"
    };

    let emptyState = null;
    if (this.state.data.rows.count() <= 1) {
      //#!if readonly === true
      const isFiltered = this.props.isViewFilterActive || this.props.isGlobalFilterActive;
      emptyState = (
        <EmptyState type="text" fixed={true}>
          <div key="content">
            <p>No items to display{isFiltered ? " for this filter" : ""}.</p>
          </div>
        </EmptyState>
      );
      //#!endif
    }

    const lockedColumnCount = tableViewUtils.getStickyColumnsCount(
      this.state.data.columns,
      this.props.containerRect.width,
      this.props.fontSize
    );

    const disableReordering = this.props.sortBy != outlineColumnIds.sequence;

    return (
      <div className="spreadsheetView" style={style}>
        <TableView
          ref={tableView => {
            this._tableView = tableView;
          }}
          fontSize={this.props.fontSize}
          data={this.state.displayData}
          lockedColumnCount={lockedColumnCount}
          calendar={this.props.calendar}
          autoAdjustEndDate={this.props.autoAdjustEndDate}
          viewId={this.props.viewId}
          isActiveView={this.props.isActiveView}
          allowColumnSelect={true}
          allowColumnResize={chooseValueForIos(false, true)}
          allowColumnReorder={true}
          allowRowDrag={allowRowDrag}
          allowRowDragNesting={true}
          availableColumns={this.props.availableColumns}
          sortedItemIds={this.props.sortedItemIds}
          selectedRowIds={this.props.selectedItemIds}
          onSelectRows={this.props.onSelectItems}
          onToggleChildren={this.onToggleChildren}
          onAddToSelection={this.props.onAddToSelection}
          onRemoveFromSelection={this.props.onRemoveFromSelection}
          onReceivedFocus={this.onReceivedFocus}
          onViewClick={this.onViewClick}
          onValueChanged={this.onTableValueChanged}
          onMultiValueChanged={this.onTableMultipleValueChanged}
          onBulkChange={this.onTableBulkChange}
          onToggleColumn={this.onToggleColumn}
          onResizeColumn={this.onResizeColumn}
          onLiveColumnDrag={this.internalMoveColumn}
          onColumnDrop={this.dispatchLastMoveAction}
          draggedRowIds={this.state.draggedItemIds}
          onStartRowDrag={this.startRowDrag}
          onRowDrop={this.onMoveRow}
          requestCanDropItems={this.requestCanDropItems}
          canItemDropOnRow={this.onCanItemDropOnRow}
          canTypeHaveSuperType={this.canTypeHaveSuperType}
          canRowDrop={canRowDrop}
          getCellOptions={this.getCellOptions}
          adjustItemEndWhenChangingStart={this.props.adjustItemEndWhenChangingStart}
          displayItems={this.props.displayItems}
          showViolatedConstraints={true}
          pulsateIconId={this.props.pulsateIconId}
          // Sorting...?
          allowSort={true}
          sortableColumnIds={this.state.sortableColumnIds}
          sortByColumnId={this.props.sortBy}
          sortInDesc={this.props.sortInDesc}
          onSortByColumn={this.onSortByColumn}
          //#!if iOS === true
// isEditMode={this.props.screenContext.isEditMode}
          //#!endif
          requestDragPreviewDataForItem={this.dragPreviewDataForItemId}
          onShowInspector={this.props.onShowInspector}
          onItemPaste={this.onItemPaste}
          getItemIdsToExpand={this.getItemIdsToExpand}
          disableReordering={disableReordering}
          reorderSortByColumn={outlineColumnIds.sequence}
        />
        {emptyState}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    localStore: state.present,
    availableColumns: selectors.getSpreadsheetViewAvailableColumns(state.present, props.viewId),
    defaultType: selectors.getSpreadsheetViewDefaultType(state.present, props.viewId),
    timelineTypeIds: typeSelectors.getTimelineTypeIds(state.present),
    calendar: calendarSelectors.getCalendar(state.present),
    autoAdjustEndDate: definitionSelectors.getAdjustItemEndWhenChangingStart(state.present),
    sortBy: appStateSelectors.getSpreadsheetViewSortBy(state.present, props.viewId),
    sortInDesc: appStateSelectors.getSpreadsheetViewSortInDesc(state.present, props.viewId),
    sortedItemIds: selectors.getSpreadsheetViewSortedItemIds(state.present, props.viewId),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    fontSize: appStateSelectors.getFontSize(state.present),
    allTypesById: definitionSelectors.getTypesById(state.present),
    itemsById: itemSelectors.getItemsById(state.present),
    propertiesById: propertySelectors.getPropertiesById(state.present),
    colorsById: colorSelectors.getColorsById(state.present),
    referencesById: referenceSelectors.getReferencesById(state.present),
    projectId: appSelectors.getProjectId(state.present),
    isActiveView: appStateSelectors.isViewActive(state.present, props.viewId),
    displayItems: displayCacheSelectors.getDisplayItemsById(state.present),
    adjustItemEndWhenChangingStart: definitionSelectors.getAdjustItemEndWhenChangingStart(state.present),
    itemSequence: sequenceSelectors.getItemSequence(state.present),
    isGlobalFilterActive: filterSelectors.isFilterWithIdActive(state.present, GLOBAL_FILTER_ID),
    isViewFilterActive: filterSelectors.isFilterWithIdActive(state.present, props.viewId),
    pulsateIconId: uiStateSelectors.getPulsateIconId(state.present)
  };
};

const readonlyActions = {
  onSelectItems: itemIds => {
    if (itemIds) {
      return appStateOperations.selectItems(itemIds);
    } else {
      return appStateOperations.clearSelection();
    }
  },
  onAddToSelection: appStateOperations.addItemsToSelection,
  onRemoveFromSelection: appStateOperations.removeItemsFromSelection,
  onToggleChildren: appStateOperations.toggleExpandSuperItem,
  onViewClick: appStateOperations.clearSelection,
  onToggleColumn: appStateOperations.toggleSpreadsheetColumn,
  onResizeColumn: appStateOperations.resizeSpreadsheetColumn,
  onBecameActiveView: appStateOperations.setActiveView,
  onSortByColumn: appStateOperations.setSpreadsheetSortBy,
  onShowInspector: uiStateOperations.showInspectorModal
};

const editingActions = {
  onCreateItemInSequence: (typeId, superId, sequenceIndex, label, viewId) => {
    trackEvent("createItemFromSpreadsheetView", "createItem", typeId);
    let selectItem = true;
    //#!if iOS === true
// selectItem = false;
    //#!endif
    if (_.isNil(sequenceIndex)) {
      return batchOperations.matchFiltersAfterCreateAction(
        batchOperations.createItemWithLabel(typeId, label, selectItem),
        viewId
      );
    } else {
      return batchOperations.matchFiltersAfterCreateAction(
        batchOperations.createItemInSequenceWithSuperAndLabel(typeId, superId, sequenceIndex, label, selectItem),
        viewId
      );
    }
  },
  onValueChanged: (itemId, columnId, value, globalState) => {
    return tableViewSelectors.createActionForValueChange(itemId, columnId, value, globalState);
  },
  onMultiValueChanged: tableViewSelectors.createActionForValueChange,
  onBulkChange: (newRowIds, changes, typeId, globalState) => {
    return tableViewSelectors.createActionForBulkChange(newRowIds, changes, typeId, globalState);
  },
  onDispatchAction: a => {
    return a;
  },
  dispatchPasteActions: actions => {
    return batchOperations.reBatchActions(actions);
  },
  onShowInspector: appStateOperations.showRightPanel
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

SpreadsheetView.propTypes = {
  viewId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  localStore: PropTypes.object.isRequired,
  availableColumns: PropTypeShapes.availableTableColumnList,
  calendar: PropTypeShapes.calendar.isRequired,
  autoAdjustEndDate: PropTypes.bool.isRequired,
  defaultType: PropTypes.string.isRequired,
  timelineTypeIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  sortBy: PropTypes.string,
  sortInDesc: PropTypes.bool,
  onSortByColumn: PropTypes.func.isRequired,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onToggleChildren: PropTypes.func.isRequired,
  onViewClick: PropTypes.func.isRequired,
  fontSize: PropTypes.number.isRequired,
  onCreateItemInSequence: PropTypes.func.isRequired,
  onValueChanged: PropTypes.func.isRequired,
  onMultiValueChanged: PropTypes.func.isRequired,
  onBulkChange: PropTypes.func.isRequired,
  onToggleColumn: PropTypes.func.isRequired,
  onResizeColumn: PropTypes.func.isRequired,
  onBecameActiveView: PropTypes.func.isRequired,
  onDispatchAction: PropTypes.func.isRequired,
  allTypesById: ImmutablePropTypes.map.isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  displayItems: ImmutablePropTypes.map.isRequired,
  adjustItemEndWhenChangingStart: PropTypes.bool.isRequired,
  itemSequence: ImmutablePropTypes.list.isRequired,
  isViewFilterActive: PropTypes.bool,
  isGlobalFilterActive: PropTypes.bool,

  pulsateIconId: PropTypes.string,

  // copy paste
  dispatchPasteActions: PropTypes.func,
  propertiesById: ImmutablePropTypes.map,
  colorsById: ImmutablePropTypes.map,
  referencesById: ImmutablePropTypes.map,

  onShowInspector: PropTypes.func,

  // Workspace context
  workViewRegistration: PropTypes.object.isRequired,

  // Comes from ContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

const normalView = withContainerContext(
  withWorkspaceContext(connect(mapStateToProps, mapDispatchToProps)(SpreadsheetView))
);

let exportedView = normalView;
//#!if iOS === true
// exportedView = withScreenContext(normalView);
//#!endif

export default exportedView;
